/**
 * Analytics page — redesigned.
 * Period picker, KPI sparkline cards, main chart with previous-period overlay,
 * top videos table, channel breakdown bars.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { analyticsApi, type TopVideo, type ChannelBreakdown } from "@/api/analytics.ts";
import { videosApi } from "@/api/videos.ts";
import {
  PageContainer, TfCard, Btn, ToggleGroup, LineChart,
  TfSpinner,
} from "@/components/v2/primitives.tsx";

type MetricKey = "views" | "watchHours" | "subs" | "revenue";

function formatNum(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
}

export function AnalyticsPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("28d");
  const [metric, setMetric] = useState<MetricKey>("views");

  const { data: summaryData, isLoading } = useQuery({
    queryKey: ["analytics", "summary", period],
    queryFn: () => analyticsApi.summary({ period }),
  });

  const { data: topVideosData } = useQuery({
    queryKey: ["analytics", "top-videos", period],
    queryFn: () => analyticsApi.topVideos({ period, limit: 6 }),
  });

  const { data: byChannelData } = useQuery({
    queryKey: ["analytics", "by-channel", period],
    queryFn: () => analyticsApi.byChannel({ period }),
  });


  const { data: videosData } = useQuery({
    queryKey: ["videos", { limit: 50 }],
    queryFn: () => videosApi.list({ limit: 50 }),
  });

  const videos = videosData?.videos ?? [];

  // Build KPI data
  const sum = (arr: number[] | undefined) => arr?.reduce((a, b) => a + b, 0) ?? 0;
  // (session audit 10-17-B, B3) no numbers reported (the snapshot table is retired): "—", never a made-up 0
  const shown = (arr: number[] | undefined, f: (n: number) => string) => (arr?.length ? f(sum(arr)) : "—");
  // (tf3-35a) no trend: the "+18%" and the others were made-up numbers, and there is no previous period to compare with
  const kpis: Array<{ id: MetricKey; label: string; value: string; data: number[]; color: string }> = [
    { id: "views", label: "Views", value: shown(summaryData?.views, formatNum), data: summaryData?.views ?? [], color: "var(--tf-accent, #4f46e5)" },
    { id: "watchHours", label: "Watch time", value: shown(summaryData?.watchHours, (n) => formatNum(n) + " hrs"), data: summaryData?.watchHours ?? [], color: "var(--tf-info, #2563eb)" },
    { id: "subs", label: "Subscribers", value: shown(summaryData?.subs, (n) => "+" + formatNum(n)), data: summaryData?.subs ?? [], color: "var(--tf-success, #16a34a)" },
    { id: "revenue", label: "Est. revenue", value: shown(summaryData?.revenue, (n) => "$" + formatNum(n)), data: summaryData?.revenue ?? [], color: "var(--tf-warning, #d97706)" },
  ];
  const activeKpi = kpis.find(k => k.id === metric) ?? kpis[0];

  if (isLoading) {
    return (
      <PageContainer title="Analytics">
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><TfSpinner size={32} /></div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Analytics" subtitle="Performance across your channels" maxWidth={1400} action={
      <div style={{ display: "flex", gap: 8 }}>
        {/* (tf3-35a) no channel picker: it filtered by the retired legacy channels; a channel's numbers are on its page */}
        <ToggleGroup value={period} onChange={setPeriod} options={[
          { value: "7d", label: "7d" },
          { value: "28d", label: "28d" },
          { value: "90d", label: "90d" },
        ]} />
      </div>
    }>
      {/* (tf3-34b) the old YouTube snapshot table is retired: the backend says where each video's numbers are now */}
      {summaryData?.note && <p className="tf-muted" role="note" style={{ fontSize: 13, margin: "0 0 16px" }}>{summaryData.note}</p>}
      {/* KPI sparkline cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
        {kpis.map(k => (
          <KpiCard key={k.id} kpi={k} active={metric === k.id} onClick={() => setMetric(k.id)} />
        ))}
      </div>

      {/* Main chart */}
      <TfCard style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
          <div>
            <div className="tf-muted" style={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{activeKpi.label} · last {period}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em" }}>{activeKpi.value}</div>
            </div>
          </div>
          <div className="tf-muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 10, height: 2, background: activeKpi.color, borderRadius: 1 }} />
              This period
            </span>
          </div>
        </div>
        <div style={{ height: 280 }}>
          <BigLineChart data={activeKpi.data} color={activeKpi.color} />
        </div>
      </TfCard>

      {/* Two-column lower */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 24, marginBottom: 24 }}>
        {/* Top videos */}
        <TfCard padding={0} style={{ overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--tf-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Top videos · {period}</h2>
            <Link to="/videos" style={{ textDecoration: "none" }}><Btn variant="ghost" size="sm">View library →</Btn></Link>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--tf-border)", background: "var(--tf-surface-2, #f5f5f5)" }}>
                  <th style={thS("40px")}>#</th>
                  <th style={thS("auto")}>Video</th>
                  <th style={thS("90px")}>Views</th>
                  <th style={thS("90px")}>Watch hrs</th>
                  <th style={thS("60px")}>CTR</th>
                </tr>
              </thead>
              <tbody>
                {(topVideosData?.videos ?? []).map((row: TopVideo, i: number) => {
                  const video = videos.find(v => v.id === row.videoId);
                  return (
                    <tr key={row.videoId} style={{ borderBottom: "1px solid var(--tf-border)", cursor: "pointer" }}
                      onClick={() => video && navigate(`/videos/${video.id}`)}>
                      <td style={tdS}><span className="tf-muted" style={{ fontSize: 11, fontFamily: "var(--tf-mono, monospace)" }}>{String(i + 1).padStart(2, "0")}</span></td>
                      <td style={tdS}>
                        <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>
                          {video?.userGuidance ?? `Video ${row.videoId}`}
                        </div>
                      </td>
                      <td style={tdS}>{formatNum(row.views)}</td>
                      <td style={tdS}>{formatNum(row.watchHours)}</td>
                      <td style={tdS}>{row.ctr.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TfCard>

        {/* Channel breakdown */}
        <TfCard>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>By channel</h2>
            <Link to="/channels" style={{ textDecoration: "none" }}><Btn variant="ghost" size="sm">All channels →</Btn></Link>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {(byChannelData?.channels ?? []).map((b: ChannelBreakdown) => {
              const ch = null as { name: string; color: string } | null;   // (tf3-35a) the breakdown is retired: none comes
              const max = Math.max(...(byChannelData?.channels ?? []).map((x: ChannelBreakdown) => x.views), 1);
              const pct = (b.views / max) * 100;
              return (
                <Link key={b.id} to={`/channels/${b.id}`} style={{ textDecoration: "none", color: "inherit", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: ch?.color ?? "var(--tf-fg-muted)" }} />
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{ch?.name ?? b.id}</span>
                    </div>
                    <span className="tf-muted" style={{ fontSize: 12 }}>{formatNum(b.views)} views</span>
                  </div>
                  <div style={{ height: 6, background: "var(--tf-surface)", borderRadius: 999, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: ch?.color ?? "var(--tf-accent)", borderRadius: 999, transition: "width .4s ease" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--tf-fg-muted)" }}>
                    <span>{formatNum(b.watchHours)} watch hrs</span>
                    <span>+{formatNum(b.subs)} subs</span>
                    <span>${formatNum(b.revenue)}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </TfCard>
      </div>

      {/* (session audit 10-17-B, B3) no "Best time to publish" heatmap: it was invented (a formula with noise), not measured */}
    </PageContainer>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

function KpiCard({ kpi, active, onClick }: {
  kpi: { id: string; label: string; value: string; data: number[]; color: string };
  active: boolean; onClick: () => void;
}) {
  return (
    <button onClick={onClick} style={{
      background: active ? "var(--tf-accent-bg, #eef2ff)" : "var(--tf-bg)",
      border: `1px solid ${active ? "var(--tf-accent, #4f46e5)" : "var(--tf-border)"}`,
      borderRadius: 10, padding: 16, cursor: "pointer", textAlign: "left",
      font: "inherit", color: "inherit",
      transition: "background .15s ease, border-color .15s ease",
      boxShadow: active ? "0 0 0 3px var(--tf-accent-bg, #eef2ff)" : "0 1px 2px rgba(0,0,0,0.04)",
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div className="tf-muted" style={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>{kpi.label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" }}>{kpi.value}</div>
      </div>
      {kpi.data.length > 0 && (
        <div style={{ height: 36, marginTop: "auto" }}>
          <LineChart data={kpi.data} height={36} color={kpi.color} />
        </div>
      )}
    </button>
  );
}

function BigLineChart({ data, color }: { data: number[]; color: string }) {
  if (!data.length) return <div className="tf-muted" style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>No data</div>;
  const max = Math.max(...data);
  const range = Math.max(max, 1);
  const w = 1000, h = 280;
  const padL = 40, padR = 12, padT = 16, padB = 26;
  const cw = w - padL - padR;
  const ch = h - padT - padB;

  const points = data.map((v, i) => {
    const x = padL + (i / Math.max(data.length - 1, 1)) * cw;
    const y = padT + ch - (v / range) * ch;
    return [x, y];
  });
  const linePath = points.map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
  const areaPath = linePath + ` L${points[points.length - 1][0]} ${padT + ch} L${padL} ${padT + ch} Z`;
  // (tf3-35a) no "previous period" line: it was this period × 0.83, a made-up comparison

  const ticks = 4;
  const tickValues = Array.from({ length: ticks + 1 }).map((_, i) => Math.round((max / ticks) * i));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
      <defs>
        <linearGradient id="big-lg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.22} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      {tickValues.map((tv, i) => {
        const y = padT + ch - (i / ticks) * ch;
        return (
          <g key={i}>
            <line x1={padL} x2={w - padR} y1={y} y2={y} stroke="var(--tf-border)" strokeDasharray="2 4" />
            <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="10" fill="var(--tf-fg-muted)" fontFamily="var(--tf-mono, monospace)">{formatNum(tv)}</text>
          </g>
        );
      })}
      <path d={areaPath} fill="url(#big-lg)" />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="4" fill={color} stroke="var(--tf-bg)" strokeWidth="2" />
    </svg>
  );
}


const thS = (w: string): React.CSSProperties => ({ width: w, textAlign: "left", padding: "10px 14px", fontWeight: 500, fontSize: 11, color: "var(--tf-fg-muted)", textTransform: "uppercase", letterSpacing: "0.05em" });
const tdS: React.CSSProperties = { padding: "10px 14px", verticalAlign: "middle" };
